/**
 * Grid owner: sidebar column + content column, the impersonation bar above
 * both, and the collapse/mobile state machine.
 * Spec: assets/handoffs/design_handoff_app_shell, Rules — shell / collapse /
 * mobile.
 *
 * Breakpoints (Rules — mobile): >=1280 sidebar expanded, manual collapse
 * wins; 768-1279 auto-collapses to the rail, manual state has no effect;
 * <768 the mobile shell (top bar + drawer) replaces the sidebar entirely.
 * Collapse persists per user, not per session — localStorage stands in here
 * for the real per-user preference store.
 *
 * The content column carries `[data-app-content]` — the actual scrolling
 * element (the page itself is `height:100vh`, `window`/`document` never
 * scroll), so any page that needs to scroll itself to a position (e.g. back
 * to top after a step change) should target
 * `document.querySelector('[data-app-content]')`, not `window.scrollTo`.
 *
 * @param {Object} sidebar - passthrough props for Sidebar
 * @param {{active,accountName,onStop}=} impersonation
 * @param {ReactNode} children - the content column
 */
import React, { useEffect, useState } from "react";
import classNames from "classnames";
import Sidebar from "../Sidebar/Sidebar";
import MobileTopBar from "../MobileTopBar/MobileTopBar";
import NavDrawer from "../NavDrawer/NavDrawer";
import ImpersonationBar from "../ImpersonationBar/ImpersonationBar";
import styles from "./AppShell.module.scss";

const COLLAPSE_STORAGE_KEY = "appshell.sidebarCollapsed";
const MOBILE_BREAKPOINT = 768;
const AUTO_COLLAPSE_BREAKPOINT = 1280;

const readStoredCollapse = () => {
  try {
    return localStorage.getItem(COLLAPSE_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

const useViewportWidth = () => {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return width;
};

const AppShell = ({ sidebar, impersonation, children }) => {
  const [manualCollapsed, setManualCollapsed] = useState(readStoredCollapse);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const width = useViewportWidth();

  const isMobile = width < MOBILE_BREAKPOINT;
  const isAutoCollapseRange = width < AUTO_COLLAPSE_BREAKPOINT;
  const collapsed = isAutoCollapseRange || manualCollapsed;

  const toggleCollapse = () => {
    setManualCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSE_STORAGE_KEY, String(next));
      } catch {
        // best-effort persistence only
      }
      return next;
    });
  };

  return (
    <div className={styles.root}>
      {impersonation?.active && (
        <ImpersonationBar accountName={impersonation.accountName} onStop={impersonation.onStop} />
      )}
      <div className={classNames(styles.body, { [styles["body--mobile"]]: isMobile })}>
        {isMobile ? (
          <>
            <MobileTopBar
              workspaceInitials={sidebar.currentWorkspace?.initials}
              workspaceIcon={sidebar.currentWorkspace?.icon}
              branding={sidebar.branding}
              onMenuClick={() => setDrawerOpen(true)}
            />
            <NavDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} sidebar={sidebar} />
          </>
        ) : (
          <Sidebar {...sidebar} collapsed={collapsed} onToggleCollapse={toggleCollapse} />
        )}
        <div className={styles.content} data-app-content>
          {children}
        </div>
      </div>
    </div>
  );
};

export default AppShell;
